import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { LocationProvider } from '@/contexts/LocationContext';
import Home from '@/pages/HomeNew';
import Auth from '@/pages/Auth';
import DateNightHub from '@/pages/DateNightHub';
import EventBrowse from '@/pages/EventBrowse';
import EventDetail from '@/pages/EventDetail';
import VenueBrowse from '@/pages/VenueBrowse';
import VenueBrowseByLocation from '@/pages/VenueBrowseByLocation';
import VenuesByAmenity from '@/pages/VenuesByAmenity';
import VenueDetail from '@/pages/VenueDetail';
import Marketplace from '@/pages/Marketplace';
import ExperienceDetail from '@/pages/ExperienceDetail';
import GoldClub from '@/pages/GoldClub';
import UserDashboard from '@/pages/UserDashboard';
import PartnerDashboard from '@/pages/PartnerDashboard';
import PartnerAnalytics from '@/pages/PartnerAnalytics';
import PartnerReservations from '@/pages/PartnerReservations';
import PartnerSettings from '@/pages/PartnerSettings';
import EventManagement from '@/pages/EventManagement';
import EventDashboard from '@/pages/EventDashboard';
import EventPageBuilder from '@/pages/EventPageBuilder';
import ExperienceManagement from '@/pages/ExperienceManagement';
import ExperienceDashboard from '@/pages/ExperienceDashboard';
import ExperiencePageBuilder from '@/pages/ExperiencePageBuilder';
import AIMarketing from '@/pages/AIMarketing';
import ItineraryBuilder from '@/pages/ItineraryBuilder';
import ItineraryView from '@/pages/ItineraryView';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';
import PartnerOnboarding from '@/pages/PartnerOnboarding';
import ApplicationStatus from '@/pages/ApplicationStatus';
import AdminDashboard from '@/pages/AdminDashboard';
import PartnerNotifications from '@/pages/PartnerNotifications';
import PartnerRedemptions from '@/pages/PartnerRedemptions';
import PartnerRevenue from '@/pages/PartnerRevenue';
import MyExperiences from '@/pages/MyExperiences';
import Guides from '@/pages/Guides';
import LocationPicker from '@/pages/LocationPicker';
import NotFound from '@/pages/NotFound';
import PartnerAuth from '@/pages/PartnerAuth';
import GoldMemberDashboard from '@/pages/GoldMemberDashboard';
import FeedbackPage from '@/pages/FeedbackPage';
import FeedbackButton from '@/components/FeedbackButton';
import CheckoutPage from '@/pages/Checkout';
import NotificationPreferences from '@/pages/NotificationPreferences';
import PrivateHireDetail from '@/pages/PrivateHireDetail';
import ExperienceBrowseByLocation from '@/pages/ExperienceBrowseByLocation';
import ExperiencesByVibe from '@/pages/ExperiencesByVibe';
import StateBrowse from '@/pages/StateBrowse';
import AutoGuidePage from '@/pages/AutoGuidePage';
import AutoGuideListPage from '@/pages/AutoGuideListPage';
import PartnerPrivateHire from '@/pages/PartnerPrivateHire';
import PartnerSyndication from '@/pages/PartnerSyndication';
import PlanView from '@/pages/PlanView';
import CreatorHubPage from '@/pages/CreatorHubPage';
import CreatorHubDashboard from '@/pages/CreatorHubDashboard';
import CreatorDirectory from '@/pages/CreatorDirectory';
import PricingPage from '@/pages/PricingPage';
import '@/index.css';

// Ambassador referral tracking
const trackReferral = () => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (ref && !sessionStorage.getItem('ref_tracked')) {
    sessionStorage.setItem('ref_code', ref);
    sessionStorage.setItem('ref_tracked', '1');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monetization/ambassadors/track/${ref}`).catch(() => {});
  }
};
if (typeof window !== 'undefined') trackReferral();

// Smart routers — detect UUID vs 2-letter state code
const isUUID = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
const ExperienceOrStateBrowse = () => {
  const { idOrState } = require('react-router-dom').useParams();
  if (isUUID(idOrState)) return <ExperienceDetail />;
  return <StateBrowse overrideState={idOrState} contentType="experiences" />;
};
const VenueOrStateBrowse = () => {
  const { idOrState } = require('react-router-dom').useParams();
  if (isUUID(idOrState)) return <VenueDetail />;
  return <StateBrowse overrideState={idOrState} contentType="venues" />;
};

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/locations" element={<LocationPicker />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/partner/register" element={<PartnerAuth />} />
          <Route path="/date-night" element={<DateNightHub />} />
          <Route path="/hubs/date-night" element={<DateNightHub />} />
          <Route path="/events" element={<EventBrowse />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/venues" element={<VenueBrowse />} />
          <Route path="/venues/amenity/:amenity" element={<VenuesByAmenity />} />
          <Route path="/venues/:idOrState" element={<VenueOrStateBrowse />} />
          <Route path="/venues/:state/:city" element={<VenueBrowseByLocation />} />
          <Route path="/venues/:state/:city/:neighborhood" element={<VenueBrowseByLocation />} />
          <Route path="/venues/:state/:city/:neighborhood/:slug" element={<VenueDetail />} />
          <Route path="/experiences" element={<Marketplace />} />
          <Route path="/experiences/vibe/:vibe" element={<ExperiencesByVibe />} />
          <Route path="/experiences/:idOrState" element={<ExperienceOrStateBrowse />} />
          <Route path="/experiences/:state/:city" element={<ExperienceBrowseByLocation />} />
          <Route path="/experiences/:state/:city/:neighborhood" element={<ExperienceBrowseByLocation />} />
          <Route path="/experiences/:state/:city/:neighborhood/:venueSlug/:slug" element={<ExperienceDetail />} />

          <Route path="/gold-club" element={<GoldClub />} />
          <Route path="/gold-club/dashboard" element={<GoldMemberDashboard />} />
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/my-experiences" element={<MyExperiences />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/auto" element={<AutoGuideListPage />} />
          <Route path="/guides/auto/:state" element={<AutoGuideListPage />} />
          <Route path="/guides/auto/:state/:city" element={<AutoGuideListPage />} />
          <Route path="/guides/auto/:state/:city/:neighborhood" element={<AutoGuideListPage />} />
          <Route path="/guides/auto/:state/:city/:guideSlug" element={<AutoGuidePage />} />
          <Route path="/guides/auto/:state/:city/:neighborhood/:guideSlug" element={<AutoGuidePage />} />
          <Route path="/itinerary/new" element={<ItineraryBuilder />} />
          <Route path="/itinerary/:id" element={<ItineraryView />} />
          {/* Partner Routes */}
          <Route path="/partner/onboarding" element={<PartnerOnboarding />} />
          <Route path="/partner/application-status" element={<ApplicationStatus />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          <Route path="/partner/analytics" element={<PartnerAnalytics />} />
          <Route path="/partner/reservations" element={<PartnerReservations />} />
          <Route path="/partner/settings" element={<PartnerSettings />} />
          <Route path="/partner/private-hire" element={<PartnerPrivateHire />} />
          <Route path="/partner/syndication/:itemId" element={<PartnerSyndication />} />
          <Route path="/partner/events" element={<EventManagement />} />
          <Route path="/partner/events/:eventId" element={<EventDashboard />} />
          <Route path="/partner/events/:eventId/builder" element={<EventPageBuilder />} />
          <Route path="/partner/experiences" element={<ExperienceManagement />} />
          <Route path="/partner/experiences/:experienceId" element={<ExperienceDashboard />} />
          <Route path="/partner/experiences/:experienceId/builder" element={<ExperiencePageBuilder />} />
          <Route path="/partner/ai-marketing" element={<AIMarketing />} />
          <Route path="/partner/notifications" element={<PartnerNotifications />} />
          <Route path="/partner/redemptions" element={<PartnerRedemptions />} />
          <Route path="/partner/revenue" element={<PartnerRevenue />} />
          {/* Custom Event URLs */}
          <Route path="/e/:customUrl" element={<EventDetail />} />
          {/* Custom Experience URLs */}
          <Route path="/exp/:customUrl" element={<ExperienceDetail />} />
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          {/* Feedback Route */}
          <Route path="/feedback" element={<FeedbackPage />} />
          {/* Payment Routes */}
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/notifications/preferences" element={<NotificationPreferences />} />
          <Route path="/private-hire/:packageId" element={<PrivateHireDetail />} />
          <Route path="/plans/:planId" element={<PlanView />} />
          <Route path="/creators/:handle" element={<CreatorHubPage />} />
          <Route path="/creators" element={<CreatorDirectory />} />
          <Route path="/my-hubs" element={<CreatorHubDashboard />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          {/* 404 Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-right" />
        <FeedbackButton />
      </BrowserRouter>
    </LocationProvider>
  </AuthProvider>
  );
}

export default App;
